import React from 'react';

import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore';
import {
	LIVE_PAGE_MODE_SWITCH_TO_EDIT,
	LIVE_PAGE_MODE_SWITCH_TO_VIEW,
	ShortcutListener,
} from '@confluence/shortcuts';

type ShortcutConfig = {
	shortcut: string;
	requiresEditPermissions?: boolean;
	requiredModeForShortcut: 'view' | 'edit';
	targetMode: 'view' | 'edit';
};

const shortcutConfigs: ShortcutConfig[] = [
	{
		requiredModeForShortcut: 'view',
		requiresEditPermissions: true,
		shortcut: LIVE_PAGE_MODE_SWITCH_TO_EDIT,
		targetMode: 'edit',
	},
	{
		requiredModeForShortcut: 'edit',
		shortcut: LIVE_PAGE_MODE_SWITCH_TO_VIEW,
		targetMode: 'view',
	},
];

export const LivePageShortcuts = ({ canEdit }: { canEdit: boolean }) => {
	const [{ mode }, { setMode }] = useLivePageMode();

	const handleModeChange = (nextMode: ShortcutConfig['targetMode']) => {
		if (mode !== nextMode) void setMode(nextMode);
	};

	const applicableShortcuts = shortcutConfigs.filter(
		({ requiredModeForShortcut, requiresEditPermissions }) => {
			// Check if the shortcut is relevant for the current mode
			const isModeApplicable = mode === requiredModeForShortcut;
			// Check if the shortcut requires any permissions. If it does, the users permissions should match the permissions to use the shortcut
			const hasEditPermissions = !requiresEditPermissions || (requiresEditPermissions && canEdit);
			return isModeApplicable && hasEditPermissions;
		},
	);

	if (applicableShortcuts.length === 0) return null;

	return (
		<>
			{applicableShortcuts.map(({ requiredModeForShortcut, shortcut, targetMode }) => (
				<ShortcutListener
					key={`${requiredModeForShortcut}-${shortcut}`}
					accelerator={shortcut}
					listener={() => handleModeChange(targetMode)}
				/>
			))}
		</>
	);
};
